import type SubstrateSearchRequest from '../../data/schema/SubstrateSearchRequest';
import type { SearchResultMessage } from '../../data/schema/SubstrateSearchResponse';
import type Message from 'owa-service/lib/contract/Message';
import {
    convertToNormalizedSubject,
    convertToItemId,
    convertUtcDateTimeToRequestTimeZone,
    convertToFlagType,
    convertRecipientToSingleRecipientType,
    convertToFolderId,
    convertSearchSortOrder,
} from './converterHelpers';
import { isFeatureEnabled } from 'owa-feature-flags';
import { convertRestIdToEwsId } from 'owa-identifiers';
import type { Result } from '@1js/3s-schema';

export default function convertSearchResultMessageToMessageType(
    searchResult: Result,
    request: SubstrateSearchRequest
): Message {
    const searchMessage = searchResult.Source as SearchResultMessage;
    const normalizedItemId =
        isFeatureEnabled('fwk-immutable-ids') && searchResult.Provenance !== 'ExchangeArchive'
            ? searchMessage.ImmutableId
                ? { Id: convertRestIdToEwsId(searchMessage.ImmutableId), ChangeKey: null }
                : searchMessage.ItemId
            : searchMessage.ItemId;
    const normalizedSubject = searchMessage?.Subject
        ? convertToNormalizedSubject(searchMessage.Subject)
        : undefined;

    return {
        Categories: searchMessage.Categories,
        ConversationId: searchMessage?.ConversationId
            ? convertToItemId(searchMessage.ConversationId)
            : undefined,
        ConversationIndex: searchMessage.ConversationIndex,
        ConversationThreadId: searchMessage.ConversationThreadId
            ? {
                  Id: searchMessage.ConversationThreadId,
                  ChangeKey: undefined,
              }
            : undefined,
        ConversationTopic: normalizedSubject,
        DateTimeReceived: searchMessage?.DateTimeReceived
            ? convertUtcDateTimeToRequestTimeZone(searchMessage.DateTimeReceived, request)
            : undefined,
        DateTimeSent:
            searchMessage.DateTimeSent &&
            convertUtcDateTimeToRequestTimeZone(searchMessage.DateTimeSent, request),
        LastModifiedTime: searchMessage?.DateTimeLastModified
            ? convertUtcDateTimeToRequestTimeZone(searchMessage.DateTimeLastModified, request)
            : undefined,
        DisplayBcc: searchMessage.DisplayBcc,
        DisplayCc: searchMessage.DisplayCc,
        DisplayTo: searchMessage.DisplayTo,
        Flag: searchMessage?.Flag ? convertToFlagType(searchMessage.Flag) : undefined,
        From: searchMessage.From
            ? convertRecipientToSingleRecipientType(searchMessage.From)
            : undefined,
        HasAttachments: searchMessage.HasAttachments,
        Hashtags: searchMessage.Hashtags,
        IconIndex: searchMessage.IconIndex,
        Importance: searchMessage.Importance,
        IsDraft: searchMessage.IsDraft,
        IsRead: searchMessage.IsRead,
        ItemClass: searchMessage.ItemClass || 'IPM.Note',
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (65,9): Type 'SearchResultItemId | { Id: string | null; ChangeKey: null; }' is not assignable to type 'ItemId | undefined'.
        // @ts-expect-error
        ItemId: normalizedItemId,
        MailboxGuids: searchMessage.MailboxGuids,
        ParentFolderDisplayName: searchMessage.ParentFolderDisplayName,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (70,9): Type 'import("packages/framework/owa-graph-schema/lib/__generated__/schema.all.interface").FolderId' is not assignable to type 'import("C:/Users/loicbe/workspace/client-web/shared/internal/owa-service/lib/contract/FolderId").default'.
        // @ts-expect-error
        ParentFolderId: searchMessage?.ParentFolderId
            ? convertToFolderId(searchMessage.ParentFolderId)
            : undefined,
        Preview: searchMessage.Preview,
        Sender: searchMessage.Sender
            ? convertRecipientToSingleRecipientType(searchMessage.Sender)
            : undefined,
        SenderSMTPAddress: searchMessage.SenderSMTPAddress,
        Sensitivity: searchMessage.Sensitivity,
        SerializedImmutableId: searchMessage.ImmutableId,
        SortOrderSource: searchMessage?.SortOrderSource
            ? convertSearchSortOrder(searchMessage.SortOrderSource)
            : undefined,
        Subject: searchMessage.Subject,
        MentionedMe: searchMessage.MentionsPreview?.IsMentioned,
        ExtendedProperty: normalizedSubject
            ? [
                  {
                      // WellKnownProperties.NormalizedSubjectProperty
                      ExtendedFieldURI: {
                          DistinguishedPropertySetId: 'Common',
                          PropertyId: 3613,
                          PropertyType: 'String',
                      },
                      Value: normalizedSubject,
                  },
              ]
            : undefined,
    };
}
